import { useEffect, useMemo, useState } from "react";
import Autosuggest from "react-autosuggest";
import {
  Configure,
  connectAutoComplete,
  InstantSearch,
} from "react-instantsearch-dom";
import classNames from "classnames";
import PropTypes from "prop-types";

import Input from "components/base/Input";
import { useFormContext } from "react-hook-form";
import { getBannedCountriesFilter } from "utils/helpers/formFields";
import { getCustomSearchClient } from "utils/helpers/search";

const mapAlgoliaLocationToLocation = (selection) => {
  const cleanSelection = JSON.parse(JSON.stringify(selection));
  delete cleanSelection.__position;
  delete cleanSelection.objectID;
  delete cleanSelection._highlightResult;

  return {
    wikiDataId: cleanSelection.wikiDataId || "",
    cityName: cleanSelection.name || "",
    stateName: cleanSelection.state_name || "",
    countryCode: cleanSelection.country_code || "",
    locationId: cleanSelection.id || "",
    latitude: cleanSelection.latitude || "",
    stateId: cleanSelection.state_id || "",
    stateCode: cleanSelection.state_code || "",
    countryName: cleanSelection.country_name || "",
    countryId: cleanSelection.country_id || "",
    longitude: cleanSelection.longitude || "",
  };
};

function Autocomplete(props) {
  const [searchValue, setSearchValue] = useState(props.currentRefinement || "");
  const { getFieldState, getValues, setValue } = useFormContext();
  const [changed, setChanged] = useState(false);
  const field = getFieldState("location");
  const value = getValues("location");

  useEffect(() => {
    setSearchValue(props.currentRefinement || "");
  }, [props.currentRefinement]);

  const onChange = (event) => {
    setChanged(true);
    setSearchValue(event.target.value);
  };

  const onBlur = () => {
    if (!searchValue) {
      setValue("location", null);
    }
  };

  const onSuggestionsFetchRequested = ({ value, reason }) => {
    if (typeof value === "string") {
      props.refine(value);
    }
  };

  const onSuggestionsClearRequested = () => {
    // onSuggestionsClearRequested needs to be implemented
    // but calling the function below in the body results
    // in the input value being lost (due to InstantSearch sending back the empty value)
    // Hence this is empty function for now
    // props.refine();
  };

  const getSuggestionValue = (hit) => {
    if (!hit) return "";
    const country = hit.country_name || "";
    const state = hit.state_name || "";
    const city = hit.name || "";
    return [country, state, city].filter(Boolean).join(", ");
  };

  const renderSuggestion = (hit) => {
    if (!hit) return "";
    const country = hit.country_name || "";
    const state = hit.state_name || "";
    const city = hit.name || "";
    return [country, state, city].filter(Boolean).join(", ");
  };

  const renderInputComponent = (inputProps) => {
    const {
      onChange: originalOnChange,
      onBlur: originalOnBlur,
      ...restInputProps
    } = inputProps;
    return (
      <Input
        id="locationSearch"
        label="City, State/Province"
        type="search"
        disabled={props.disabled}
        classOverrides={classNames({
          "!border-functionalDanger": !!field?.error,
          "!border-gray-600": value && !changed,
          "!border-zestygreen": !field?.error && !!value && changed,
        })}
        onChange={({ value }) => {
          originalOnChange({ target: { value } });
        }}
        onBlur={(e) => {
          originalOnBlur?.(e);
          onBlur();
        }}
        {...restInputProps}
      />
    );
  };

  const renderSuggestionsContainer = ({ containerProps, children, query }) => {
    if (!children) {
      return <div {...containerProps}>{children}</div>;
    }

    return (
      <div
        {...containerProps}
        className={classNames(
          containerProps.className,
          "w-full absolute z-50 border border-gray-300 bg-white rounded-md p-4 -mt-3.5 mb-3 max-h-60 overflow-hidden [&>ul]:overflow-y-auto [&>ul]:max-h-[calc(15rem-2rem)] [&>ul]:pr-2 [&>ul>li]:cursor-pointer [&>ul>li:hover]:bg-gray-100 [&>ul>li]:p-2"
        )}
      >
        {children}
      </div>
    );
  };

  const inputProps = {
    onChange,
    value: searchValue || "",
  };

  return (
    <Autosuggest
      suggestions={props.hits || []}
      onSuggestionsFetchRequested={onSuggestionsFetchRequested}
      onSuggestionsClearRequested={onSuggestionsClearRequested}
      getSuggestionValue={getSuggestionValue}
      renderSuggestion={renderSuggestion}
      inputProps={inputProps}
      renderInputComponent={renderInputComponent}
      renderSuggestionsContainer={renderSuggestionsContainer}
      onSuggestionSelected={(e, { suggestion }) => {
        const mappedSelection = mapAlgoliaLocationToLocation(suggestion);
        setValue("location", mappedSelection);
        props.onSuggestionSelected(e, { suggestion });
      }}
      focusInputOnSuggestionClick={false}
    />
  );
}

const CustomAutocomplete = connectAutoComplete(Autocomplete);

export default function SearchLocations({ location, setLocation, disabled }) {
  const [search, setSearch] = useState({ query: "", page: 1 });

  const bannedCountriesFilter = useMemo(() => {
    return getBannedCountriesFilter();
  }, []);

  useEffect(() => {
    if (location) {
      setSearch({
        query: `${location.countryName}, ${location.stateName}, ${location.cityName}`,
        page: 1,
      });
    }
  }, [location]);

  const onSuggestionSelected = (e, { suggestion }) => {
    const mappedSelection = mapAlgoliaLocationToLocation(suggestion);
    setLocation(mappedSelection);
  };

  const searchClient = getCustomSearchClient(undefined, undefined, {
    infiniteCache: true,
  });

  return (
    <div className="relative">
      <InstantSearch
        searchClient={searchClient}
        indexName={process.env.REACT_APP_OS_CITIES_INDEX_NAME}
        searchState={search}
        onSearchStateChange={(s) => {
          setSearch(s);
          if (!s.query) {
            setLocation(null);
          }
        }}
      >
        <CustomAutocomplete
          onSuggestionSelected={onSuggestionSelected}
          disabled={disabled}
        />
        <Configure filters={bannedCountriesFilter} />
      </InstantSearch>
    </div>
  );
}

SearchLocations.propTypes = {
  location: PropTypes.object,
  setLocation: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

SearchLocations.defaultProps = {
  disabled: false,
};
